import React, { useState, useRef, useEffect } from 'react';
import { FiUpload, FiX } from 'react-icons/fi';

import { FiFileText, FiImage, FiFile, FiFilePdf } from "react-icons/fi";
import { Code, FileArchive, FileAudio, FileSpreadsheet, FileVideo } from "lucide-react";
import { FaRegFileWord, FaRegFilePdf } from "react-icons/fa6";

import useDeviceDetection from "./useDeviceDetection"
import Toast from './Toast';

const DragDropUploader = ({
    onFilesChange,
    clearFiles = false,
    acceptedTypes = "",
    showFileOptions = false,  // NEW
    availableCategories = [], // NEW
    availableQuoteTypes = [],  // NEW
    jobDepartments = []
}) => {
    const { isMobile, isDesktop } = useDeviceDetection();
    const [files, setFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    // Clear files when clearFiles prop changes
    useEffect(() => {
        if (clearFiles) {
            setFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [clearFiles]);

    // Update parent component when files change
    useEffect(() => {
        // Return raw files for backward compatibility when showFileOptions is false
        if (!showFileOptions) {
            onFilesChange(files);
        } else {
            // Return files with metadata
            onFilesChange(files);
        }
    }, [files, showFileOptions, onFilesChange]);

    const updateFileMetadata = (index, field, value) => {
        setFiles(prevFiles =>
            prevFiles.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        );
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Only set isDragOver to false if we're actually leaving the drop zone
        // Check if the related target is outside the current target
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true); // Keep it blue while dragging over
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        addFiles(droppedFiles);
    };

    const handleFileInput = (e) => {
        const selectedFiles = Array.from(e.target.files);
        addFiles(selectedFiles);
    };

    const addFiles = (newFiles) => {
        const hasAcceptedTypes =
            acceptedTypes && acceptedTypes.trim().length > 0;

        const allowedExtensions = hasAcceptedTypes
            ? acceptedTypes
                .split(',')
                .map(t => t.trim().toLowerCase())
            : null;

        const validFiles = newFiles.filter(file => {
            if (!hasAcceptedTypes) return true; // ✅ accept everything

            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            return allowedExtensions.includes(fileExtension);
        });

        // NEW: Wrap files in metadata object if showFileOptions is true
        const filesToAdd = showFileOptions
            ? validFiles.map(file => ({
                file: file,
                category: availableCategories[0] || '',
                quoteType: availableQuoteTypes[0] || ''
            }))
            : validFiles;

        setFiles(prevFiles => [...prevFiles, ...filesToAdd]);
    };

    const removeFile = (indexToRemove) => {
        setFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    function capitalizeFirstLetter(val) {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }



    const FILE_ICON_MAP = {
        image: {
            extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'],
            icon: <FiImage />
        },
        pdf: {
            extensions: ['pdf'],
            icon: <FaRegFilePdf />
        },
        word: {
            extensions: ['doc', 'docx'],
            icon: <FaRegFileWord />
        },
        excel: {
            extensions: ['xls', 'xlsx', 'csv'],
            icon: <FileSpreadsheet size={18} />
        },
        audio: {
            extensions: ['mp3', 'wav', 'ogg', 'flac', 'aac'],
            icon: <FileAudio />
        },
        video: {
            extensions: ['mp4', 'mov', 'avi', 'mkv', 'webm'],
            icon: <FileVideo />
        },
        archive: {
            extensions: ['zip', 'rar', '7z', 'tar', 'gz'],
            icon: <FileArchive />
        },
        code: {
            extensions: ['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'py', 'java', 'php'],
            icon: <Code size={18} />
        },
        text: {
            extensions: ['txt', 'md', 'log'],
            icon: <FiFileText />
        }
    };


    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();

        for (const type in FILE_ICON_MAP) {
            if (FILE_ICON_MAP[type].extensions.includes(extension)) {
                return FILE_ICON_MAP[type].icon;
            }
        }

        return <FiFile />; // default icon
    };

    return (
        <div className="w-full">
            {/* Drop Zone — using <label> so iOS fires click on first tap (div onClick requires two taps) */}
            <label
                className={`block border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer [touch-action:manipulation] ${isDragOver
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300'
                    }`}

                // onDragEnter={handleDragEnter}
                // onDragLeave={handleDragLeave}
                // onDragOver={handleDragOver}
                // onDrop={handleDrop}
                {...(!isMobile && {
                    onDragEnter: handleDragEnter,
                    onDragLeave: handleDragLeave,
                    onDragOver: handleDragOver,
                    onDrop: handleDrop
                })}
                onTouchEnd={(e) => {
                    e.preventDefault();
                    fileInputRef.current?.click();
                }}
            >
                <FiUpload className={`w-12 h-12 mx-auto mb-4 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className={`text-lg font-medium mb-2 ${isDragOver ? 'text-blue-600' : 'text-gray-600'}`}>
                    {isDesktop ?
                        <>{isDragOver ? 'Drop files here' : 'Drag & drop files here'}</>
                        :
                        "Tap to browse your device"
                    }
                </p>
                <p className="text-sm text-gray-500 mb-4">
                    {isDesktop ? "or click to browse your computer" : "and add files"}
                </p>
                <p className="text-xs text-gray-400">
                    Supported: PDF, DOC, DOCX, JPG, JPEG, PNG, IV, TXT, HTML, EML, ZIP, RAR, MP3, MP4
                </p>

                {/* Input is a direct child of the label — browser natively activates it on first tap */}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={acceptedTypes}
                    onChange={handleFileInput}
                    className="hidden"
                />
            </label>

            {/* File List */}
            {files.length > 0 && (
                <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Selected Files ({files.length})
                    </h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {files.map((item, index) => {
                            const file = showFileOptions ? item.file : item;
                            const fileName = file.name;
                            const fileSize = file.size;

                            return (
                                <div
                                    key={index}
                                    className="p-3 bg-gray-50 rounded-lg border"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                                            <span className="text-2xl flex-shrink-0">{getFileIcon(fileName)}</span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {fileName}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {formatFileSize(fileSize)}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFile(index);
                                            }}
                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded flex-shrink-0"
                                        >
                                            <FiX className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {showFileOptions && (
                                        <div className="flex flex-row gap-3 mt-3 pt-3 border-t border-gray-200">
                                            {availableCategories.length > 0 && (
                                                <div className='w-64'>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        Category
                                                    </label>
                                                    <select
                                                        value={item.category}
                                                        onChange={(e) => updateFileMetadata(index, 'category', e.target.value)}
                                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    >
                                                        {availableCategories.map(cat => (
                                                            <option key={cat} value={cat}>{cat === "initial" ? "Job File" : capitalizeFirstLetter(cat)}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            {(availableQuoteTypes.length > 0 && item?.category === "design") && (
                                                <div className='w-64'>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        Quote Type
                                                    </label>
                                                    <select
                                                        value={item.quoteType}
                                                        onChange={(e) => updateFileMetadata(index, 'quoteType', e.target.value)}
                                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    >
                                                        {availableQuoteTypes.map(type => {
                                                            return (
                                                                <option key={type} value={type}>{capitalizeFirstLetter(type)}</option>
                                                            )
                                                        }
                                                        )}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DragDropUploader;